import folderIdFactory from 'owa-service/lib/factory/folderId';
import folderNameToId from 'owa-session-store/lib/utils/folderNameToId';
import getCalendarViewOperation from 'owa-service/lib/operation/getCalendarViewOperation';
import getCalendarViewRequest from 'owa-service/lib/factory/getCalendarViewRequest';
import GetCalendarViewResponse from 'owa-service/lib/contract/GetCalendarViewResponse';
import targetFolderId from 'owa-service/lib/factory/targetFolderId';
import { createRequestQueue, RequestQueue } from 'owa-service-utils';
import { OwaDate, asDate } from 'owa-datetime';
import { getJsonRequestHeader } from 'owa-service/lib/ServiceRequestUtils';
import { traceAndReject } from 'error-handling';
import { getMiniEwsRequestString } from './getMiniEWSRequestString';
import { createAppContext } from 'mini-app-context/lib/utils/createAppContext';
import { getTimeFromFetchStart } from 'mini-performance/lib/utils/getTimeFromFetchStart';

const HEADER_EXPLICIT_LOGON_USER: string = 'X-OWA-ExplicitLogonUser';
const HEADER_ANCHOR_MAILBOX: string = 'X-AnchorMailbox';

const MAXIMUM_NUM_REQUESTS = 6;
let requestQueue: RequestQueue = null;

const prefetchMap = {};
let cacheHitLogged = false;

function generateCacheKey(rangeStart: OwaDate, rangeEnd: OwaDate, folderId: string) {
    return asDate(rangeStart).getTime() + '_' + asDate(rangeEnd).getTime() + '_' + folderId;
}

/**
 * Makes the GetCalendarView call to the server to get calendar items for default calendar
 * @param {Date} rangeStart start of request range
 * @param {Date} rangeEnd end of request range
 * @param {string} folderId folder id of requested calendar
 * @param {string} explicitLogonEmail smtp address of request mailbox to which the service request should be routed.
 For linked calendars, pass the email otherwise, the CalendarId.mailboxInfo will take care of it.
 * @returns promise of GetCalendarViewResponse type
 */
export const getCalendarViewService = async (
    rangeStart: OwaDate,
    rangeEnd: OwaDate,
    folderId: string = folderNameToId('calendar'),
    explicitLogonEmail?: string | null,
    prefetch?: boolean
): Promise<GetCalendarViewResponse> => {
    const key = generateCacheKey(rangeStart, rangeEnd, folderId);
    const appContext = createAppContext();

    // Currently disabling consuming prefetch promise from explicit logon email as need to find
    // if there is any deviation in response in WW when explicit logon email is not sent.
    if (!prefetch && prefetchMap[key] && !explicitLogonEmail) {
        const responsePromise = prefetchMap[key];
        if (!cacheHitLogged) {
            cacheHitLogged = true;
            appContext?.signal('GET_CALENDAR_VIEW_CACHE_HIT', {
                time: getTimeFromFetchStart(),
            });
        }
        delete prefetchMap[key];
        return responsePromise;
    }

    let options = null;

    if (explicitLogonEmail) {
        options = {
            headers: <any>{
                [HEADER_EXPLICIT_LOGON_USER]: explicitLogonEmail,
                [HEADER_ANCHOR_MAILBOX]: explicitLogonEmail,
            },
        };
    }

    if (!requestQueue) {
        requestQueue = createRequestQueue(MAXIMUM_NUM_REQUESTS);
    }

    const promiseRef = requestQueue.add(() =>
        getCalendarViewOperation(
            {
                Header: getJsonRequestHeader(),
                Body: getCalendarViewRequest({
                    CalendarId: targetFolderId({
                        BaseFolderId: folderIdFactory({ Id: folderId }),
                    }),
                    RangeStart: getMiniEwsRequestString(rangeStart),
                    RangeEnd: getMiniEwsRequestString(rangeEnd),
                    // Commented for now, till we upgrade owa-service package.
                    //IncludeDeclinedMeetings: includeDeclinedMeetings,
                }),
            },
            options
        )
            .then(response => response && response.Body)
            .catch(error => traceAndReject('getCalendarViewService', error))
    );

    // Do this logging when explicity logon email request is present and a prefetch promise is present
    // This is a temporary code and will be removed after required data has been found to make a decision.
    if (explicitLogonEmail && prefetchMap[key]) {
        const activePromise = prefetchMap[key];
        // Clear the prefetch map to avoid logging again
        delete prefetchMap[key];
        try {
            Promise.all([promiseRef, activePromise]).then(data => {
                try {
                    const [currentData, earlyFetchData] = data;
                    let jsonDiffFailureFlag = false;
                    if (JSON.stringify(currentData) !== JSON.stringify(earlyFetchData)) {
                        jsonDiffFailureFlag = true;
                    }

                    createAppContext()?.signal(
                        'EXPLICIT_LOGON_INCONSISTENCY',
                        {
                            itemsCountLength: currentData.Items?.length,
                            earlyFetchCountLength: earlyFetchData.Items?.length,
                            jsonDiffFailure: jsonDiffFailureFlag,
                        },
                        {
                            isCore: true,
                        }
                    );
                } catch (error) {
                    // to handle any stringify error
                }
            });
        } catch (error) {
            // To prevent any error leak from this logging
        }
    }

    if (prefetch) {
        prefetchMap[key] = promiseRef;
    }

    return promiseRef;
};
